import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import MultiUserSelect from '../components/MultiUserSelect';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { 
  Plus, ChevronLeft, ChevronRight, Calendar, 
  Check, Trash2, Edit2, User, ArrowLeft,
  Megaphone, Globe, Briefcase, Share2, Users, Clock, FileText, Repeat, MessageSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import UserAvatar from '../components/UserAvatar';
import TaskDetailDialog from '../components/TaskDetailDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TEAMS = [
  { 
    id: 'social_media', 
    label: 'Social Media', 
    icon: Share2, 
    color: 'from-[#0EA5E9] to-[#0369A1]',
    lightColor: 'bg-[#E0F2FE]',
    textColor: 'text-[#0369A1]',
    borderColor: 'border-l-[#0369A1]',
    description: 'Instagram, Facebook, LinkedIn'
  },
  { 
    id: 'marketing', 
    label: 'Marketing', 
    icon: Megaphone, 
    color: 'from-[#A855F7] to-[#7E22CE]',
    lightColor: 'bg-[#F3E8FF]',
    textColor: 'text-[#7E22CE]',
    borderColor: 'border-l-[#7E22CE]',
    description: 'Campaigns, Events, PR'
  },
  { 
    id: 'website', 
    label: 'Website', 
    icon: Globe, 
    color: 'from-[#10B981] to-[#047857]',
    lightColor: 'bg-[#D1FAE5]',
    textColor: 'text-[#047857]',
    borderColor: 'border-l-[#047857]',
    description: 'Content, Updates, SEO'
  },
  { 
    id: 'administration', 
    label: 'Administration', 
    icon: Briefcase, 
    color: 'from-[#F97316] to-[#C2410C]',
    lightColor: 'bg-[#FFEDD5]',
    textColor: 'text-[#C2410C]',
    borderColor: 'border-l-[#C2410C]',
    description: 'Organization, Finance, HR'
  }
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'bg-zinc-100 text-zinc-600', icon: '○' },
  { id: 'medium', label: 'Medium', color: 'bg-[#FEF3C7] text-[#B45309]', icon: '◐' },
  { id: 'high', label: 'High', color: 'bg-[#FEE2E2] text-[#D93025]', icon: '●' }
];

const PlanPage = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [plan, setPlan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [quickAddDay, setQuickAddDay] = useState(null);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddAssignees, setQuickAddAssignees] = useState([]);
  const [quickAddPriority, setQuickAddPriority] = useState('medium');
  const [expandedDescIds, setExpandedDescIds] = useState({});
  const [detailTask, setDetailTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: [],
    team: 'social_media',
    day: 'monday',
    priority: 'medium',
    recurrence_type: 'none',
    recurrence_interval_weeks: 2,
    recurrence_daily_weekdays_only: true,
    recurrence_end_date: ''
  });

  const getLocale = () => i18n.language === 'de' ? de : enUS;
  const weekStart = format(currentWeek, 'yyyy-MM-dd');

  const fetchPlanData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/plans/week/${weekStart}`, {
        headers: getAuthHeader()
      });
      setPlan(response.data.plan);
      setTasks(response.data.tasks);
    } catch (error) {
      console.error('Failed to fetch plan data:', error);
    } finally {
      setLoading(false);
    }
  }, [weekStart, getAuthHeader]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchPlanData();
    fetchUsers();
  }, [fetchPlanData, fetchUsers]);

  const handlePrevWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const handleNextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));
  const handleToday = () => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const getTasksForTeam = (teamId) => tasks.filter(t => t.team === teamId);
  const getTasksForTeamAndDay = (teamId, day) => tasks.filter(t => t.team === teamId && t.day === day).sort((a, b) => a.order - b.order);

  const openCreateDialog = (day = 'monday') => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      assigned_to: [],
      team: selectedTeam?.id || 'social_media',
      day,
      priority: 'medium',
      recurrence_type: 'none',
      recurrence_interval_weeks: 2,
      recurrence_daily_weekdays_only: true,
      recurrence_end_date: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      assigned_to: Array.isArray(task.assigned_to) ? task.assigned_to : (task.assigned_to ? [task.assigned_to] : []),
      team: task.team,
      day: task.day,
      priority: task.priority || 'medium',
      recurrence_type: task.recurrence_type || 'none',
      recurrence_interval_weeks: task.recurrence_interval_weeks || 2,
      recurrence_daily_weekdays_only: task.recurrence_daily_weekdays_only ?? true,
      recurrence_end_date: task.recurrence_end_date || ''
    });
    setDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!formData.title.trim()) {
      toast.error(t('plan.titleRequired'));
      return;
    }

    try {
      const payload = {
        ...formData,
        assigned_to: formData.assigned_to && formData.assigned_to.length > 0 ? formData.assigned_to : null,
        recurrence_type: formData.recurrence_type === 'none' ? null : formData.recurrence_type,
        recurrence_interval_weeks: formData.recurrence_type === 'interval' ? Number(formData.recurrence_interval_weeks) : null,
        recurrence_end_date: formData.recurrence_end_date || null,
        recurrence_daily_weekdays_only: formData.recurrence_type === 'daily' ? !!formData.recurrence_daily_weekdays_only : false,
      };
      if (editingTask) {
        // If editing a virtual series instance, edit the origin series
        const targetId = editingTask.instance_id ? editingTask.id : editingTask.id;
        await axios.put(`${API_URL}/plans/tasks/${targetId}`, payload, { headers: getAuthHeader() });
        toast.success(t('plan.taskUpdated'));
      } else {
        await axios.post(`${API_URL}/plans/${plan.id}/tasks`, payload, { headers: getAuthHeader() });
        toast.success(t('plan.taskCreated'));
      }
      setDialogOpen(false);
      fetchPlanData();
    } catch (error) {
      toast.error(t('plan.error'));
    }
  };

  const handleQuickAdd = async (day) => {
    if (!quickAddTitle.trim()) return;
    
    try {
      await axios.post(`${API_URL}/plans/${plan.id}/tasks`, {
        title: quickAddTitle,
        team: selectedTeam.id,
        day,
        priority: quickAddPriority,
        assigned_to: quickAddAssignees.length > 0 ? quickAddAssignees : null
      }, { headers: getAuthHeader() });
      
      toast.success(t('plan.taskCreated'));
      setQuickAddTitle('');
      setQuickAddAssignees([]);
      setQuickAddPriority('medium');
      setQuickAddDay(null);
      fetchPlanData();
    } catch (error) {
      toast.error(t('plan.error'));
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_URL}/plans/tasks/${taskId}`, { headers: getAuthHeader() });
      toast.success(t('plan.taskDeleted'));
      fetchPlanData();
    } catch (error) {
      toast.error(t('plan.error'));
    }
  };

  const handleToggleComplete = async (task) => {
    try {
      if (task._isSeriesInstance) {
        // Per-week completion for a virtual instance
        const ws = format(currentWeek, 'yyyy-MM-dd');
        await axios.put(
          `${API_URL}/plans/tasks/${task.id}/instance?week_start=${ws}&day=${task.day}`,
          { completed: !task.completed },
          { headers: getAuthHeader() }
        );
      } else {
        await axios.put(`${API_URL}/plans/tasks/${task.id}`, { completed: !task.completed }, { headers: getAuthHeader() });
      }
      fetchPlanData();
    } catch (error) {
      toast.error(t('plan.error'));
    }
  };

  const handleQuickPriorityChange = async (task, newPriority) => {
    try {
      await axios.put(`${API_URL}/plans/tasks/${task.id}`, { priority: newPriority }, { headers: getAuthHeader() });
      fetchPlanData();
    } catch (error) {
      toast.error(t('plan.error'));
    }
  };

  const cyclePriority = (currentPriority) => {
    const priorities = ['low', 'medium', 'high'];
    const currentIndex = priorities.indexOf(currentPriority);
    return priorities[(currentIndex + 1) % priorities.length];
  };

  const getDayDate = (dayIndex) => addDays(currentWeek, dayIndex);
  const getDayLabel = (day, index) => {
    const date = getDayDate(index);
    return {
      dayName: format(date, 'EEE', { locale: getLocale() }),
      fullDay: format(date, 'EEEE', { locale: getLocale() }),
      dateStr: format(date, 'd', { locale: getLocale() }),
      isToday: format(new Date(), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    };
  };

  const getTeamInfo = (teamId) => TEAMS.find(t => t.id === teamId) || TEAMS[0];
  const getPriorityInfo = (priorityId) => PRIORITIES.find(p => p.id === priorityId) || PRIORITIES[1];

  // Team Overview View
  if (!selectedTeam) {
    return (
      <Layout>
        <div data-testid="plan-page" className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('plan.title')}
            </h1>
            <p className="text-zinc-600">{t('plan.selectTeam')}</p>
          </div>

          {/* Week Navigation */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <Button variant="outline" size="sm" onClick={handlePrevWeek} className="rounded-full h-10 w-10 p-0">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="bg-white px-6 py-3 rounded-2xl border border-zinc-200 text-center min-w-[280px]">
              <div className="font-semibold text-zinc-900">
                {format(currentWeek, 'd. MMM', { locale: getLocale() })} - {format(addDays(currentWeek, 4), 'd. MMM yyyy', { locale: getLocale() })}
              </div>
              <div className="text-sm text-zinc-500">{t('plan.weekOf')} {format(currentWeek, 'wo', { locale: getLocale() })}</div>
            </div>
            <Button variant="outline" size="sm" onClick={handleNextWeek} className="rounded-full h-10 w-10 p-0">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Team Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAMS.map((team, index) => {
              const teamTasks = getTasksForTeam(team.id);
              const completedTasks = teamTasks.filter(t => t.completed).length;
              const assignedToMe = teamTasks.filter(t => Array.isArray(t.assigned_to) && t.assigned_to.includes(user?.id)).length;
              
              return (
                <motion.div
                  key={team.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedTeam(team)}
                  className="cursor-pointer group"
                >
                  <div className={`bg-gradient-to-br ${team.color} p-6 rounded-3xl text-white shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300`}>
                    {/* Icon */}
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
                      <team.icon className="w-7 h-7" />
                    </div>
                    
                    {/* Team Name */}
                    <h2 className="text-xl font-bold mb-1">{t(`plan.teams.${team.id}`)}</h2>
                    <p className="text-white/70 text-sm mb-4">{team.description}</p>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/20">
                      <div>
                        <div className="text-2xl font-bold">{teamTasks.length}</div>
                        <div className="text-xs text-white/70">{t('plan.totalTasks')}</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{completedTasks}</div>
                        <div className="text-xs text-white/70">{t('plan.completed')}</div>
                      </div>
                    </div>
                    
                    {/* My Tasks Badge */}
                    {assignedToMe > 0 && (
                      <div className="mt-4 bg-white/20 rounded-full px-3 py-1.5 inline-flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span className="text-sm font-medium">{assignedToMe} {t('plan.myTasks')}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Quick Stats */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 text-center">
              <div className="text-3xl font-bold text-zinc-900">{tasks.length}</div>
              <div className="text-sm text-zinc-500">{t('plan.totalTasks')}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 text-center">
              <div className="text-3xl font-bold text-[#047857]">{tasks.filter(t => t.completed).length}</div>
              <div className="text-sm text-zinc-500">{t('plan.completed')}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 text-center">
              <div className="text-3xl font-bold text-[#D93025]">{tasks.filter(t => !t.completed).length}</div>
              <div className="text-sm text-zinc-500">{t('plan.pending')}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 text-center">
              <div className="text-3xl font-bold text-[#0369A1]">{tasks.filter(t => Array.isArray(t.assigned_to) && t.assigned_to.includes(user?.id) && !t.completed).length}</div>
              <div className="text-sm text-zinc-500">{t('plan.myPending')}</div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Team Detail View
  const teamInfo = getTeamInfo(selectedTeam.id);
  const teamTasks = getTasksForTeam(selectedTeam.id);
  const teamAssignments = users
    .map(u => {
      const userTasks = teamTasks.filter(t => Array.isArray(t.assigned_to) && t.assigned_to.includes(u.id));
      return { user: u, userTasks, completed: userTasks.filter(t => t.completed).length };
    })
    .filter(a => a.userTasks.length > 0);
  
  return (
    <Layout>
      <div data-testid="plan-team-detail" className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedTeam(null)}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${selectedTeam.color} flex items-center justify-center text-white`}>
              <selectedTeam.icon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">{t(`plan.teams.${selectedTeam.id}`)}</h1>
              <p className="text-sm text-zinc-500">{selectedTeam.description}</p>
            </div>
          </div>
          
          <Button 
            onClick={() => openCreateDialog()} 
            className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('plan.newTask')}
          </Button>
        </div>

        {/* Week Navigation - Compact */}
        <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-zinc-200 mb-6">
          <Button variant="ghost" size="sm" onClick={handlePrevWeek} className="rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <span className="font-semibold text-zinc-900">
              {format(currentWeek, 'd. MMM', { locale: getLocale() })} - {format(addDays(currentWeek, 6), 'd. MMM', { locale: getLocale() })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleToday} className="rounded-full text-xs">
              {t('plan.today')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleNextWeek} className="rounded-full">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Days Grid - max 4 per row for better readability */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {DAYS.map((day, dayIndex) => {
            const { dayName, fullDay, dateStr, isToday } = getDayLabel(day, dayIndex);
            const dayTasks = getTasksForTeamAndDay(selectedTeam.id, day);
            const isWeekend = dayIndex >= 5;
            
            return (
              <div 
                key={day} 
                className={`bg-white rounded-2xl border-2 overflow-hidden min-w-[180px] ${isToday ? 'border-[#D93025]' : isWeekend ? 'border-zinc-300 border-dashed' : 'border-zinc-200'}`}
              >
                {/* Day Header */}
                <div className={`p-3 ${isToday ? 'bg-[#D93025] text-white' : isWeekend ? 'bg-zinc-100' : 'bg-zinc-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-sm sm:text-base">{dayName}</div>
                      <div className={`text-xs ${isToday ? 'text-white/70' : 'text-zinc-500'}`}>{dateStr}.</div>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isToday ? 'bg-white/20' : 'bg-zinc-200'} ${isToday ? 'text-white' : 'text-zinc-600'}`}>
                      {dayTasks.length}
                    </div>
                  </div>
                </div>
                
                {/* Tasks */}
                <div className="p-2 space-y-2 min-h-[120px]">
                  <AnimatePresence>
                    {dayTasks.map(task => {
                      const priority = getPriorityInfo(task.priority);
                      
                      return (
                        <motion.div
                          key={task.instance_id || task.id}
                          layout
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          onClick={() => setDetailTask(task)}
                          data-testid={`plan-task-${task.instance_id || task.id}`}
                          className={`p-3 rounded-xl border-l-4 ${teamInfo.borderColor} bg-zinc-50 hover:bg-zinc-100 transition-colors group cursor-pointer`}
                        >
                          <div className="flex items-start gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleToggleComplete(task); }}
                              className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                task.completed 
                                  ? 'bg-[#047857] border-[#047857] text-white' 
                                  : 'border-zinc-300 hover:border-[#D93025]'
                              }`}
                            >
                              {task.completed && <Check className="w-3 h-3" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium text-sm ${task.completed ? 'line-through text-zinc-400' : 'text-zinc-900'}`}>
                                {task.title}
                              </div>
                              
                              {/* Assignees & Priority */}
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {Array.isArray(task.assigned_to_names) && task.assigned_to_names.length > 0 && (
                                  <div className="flex items-center -space-x-1.5">
                                    {task.assigned_to_names.slice(0, 3).map((name, idx) => {
                                      const uid = Array.isArray(task.assigned_to) ? task.assigned_to[idx] : null;
                                      const u = users.find(x => x.id === uid);
                                      return (
                                        <UserAvatar
                                          key={uid || `${task.id}-${name}-${idx}`}
                                          user={u || { id: uid, name }}
                                          size="xs"
                                          ring
                                        />
                                      );
                                    })}
                                    {task.assigned_to_names.length > 3 && (
                                      <span className="w-5 h-5 rounded-full bg-zinc-300 text-zinc-700 flex items-center justify-center text-[9px] font-bold ring-2 ring-zinc-50">
                                        +{task.assigned_to_names.length - 3}
                                      </span>
                                    )}
                                  </div>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickPriorityChange(task, cyclePriority(task.priority));
                                  }}
                                  className={`text-xs px-2 py-0.5 rounded-full ${priority.color} hover:ring-2 hover:ring-offset-1 hover:ring-zinc-300 transition-all cursor-pointer`}
                                  title={t('plan.clickToChangePriority')}
                                >
                                  {priority.icon} {t(`plan.priority.${task.priority}`)}
                                </button>
                                {task.recurrence_type && (
                                  <span
                                    className="text-xs px-1.5 py-0.5 rounded-full bg-[#FEE2E2] text-[#D93025] inline-flex items-center gap-1"
                                    title={t(`recurrence.type.${task.recurrence_type}`)}
                                    data-testid={`plan-task-recurring-${task.instance_id || task.id}`}
                                  >
                                    <Repeat className="w-3 h-3" />
                                  </span>
                                )}
                                {task.description && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedDescIds(prev => ({ ...prev, [task.id]: !prev[task.id] }));
                                    }}
                                    className={`text-xs px-1.5 py-0.5 rounded-full transition-colors inline-flex items-center gap-1 ${
                                      expandedDescIds[task.id]
                                        ? 'bg-zinc-200 text-zinc-800'
                                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                                    }`}
                                    title={t('plan.showDescription')}
                                    aria-expanded={!!expandedDescIds[task.id]}
                                    data-testid={`plan-task-desc-toggle-${task.id}`}
                                  >
                                    <FileText className="w-3 h-3" />
                                  </button>
                                )}
                              </div>

                              {/* Description (collapsed by default) */}
                              {task.description && expandedDescIds[task.id] && (
                                <p
                                  className="text-xs text-zinc-600 mt-2 whitespace-pre-wrap bg-white border border-zinc-200 rounded-lg px-2 py-1.5"
                                  data-testid={`plan-task-desc-content-${task.id}`}
                                >
                                  {task.description}
                                </p>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditDialog(task); }}
                                className="p-1.5 hover:bg-white rounded-lg"
                                title={t('common.edit')}
                              >
                                <Edit2 className="w-3.5 h-3.5 text-zinc-500" />
                              </button>
                              {!task._isSeriesInstance && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                                  className="p-1.5 hover:bg-red-50 rounded-lg"
                                  title={t('common.delete')}
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                </button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                  
                  {/* Quick Add */}
                  {quickAddDay === day ? (
                    <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200 space-y-2">
                      {/* Title Input */}
                      <Input
                        value={quickAddTitle}
                        onChange={(e) => setQuickAddTitle(e.target.value)}
                        placeholder={t('plan.titlePlaceholder')}
                        className="h-9 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && quickAddTitle.trim()) handleQuickAdd(day);
                          if (e.key === 'Escape') { 
                            setQuickAddDay(null); 
                            setQuickAddTitle(''); 
                            setQuickAddAssignees([]);
                            setQuickAddPriority('medium');
                          }
                        }}
                      />
                      
                      {/* Assignees (Multi) */}
                      <MultiUserSelect
                        users={users}
                        value={quickAddAssignees}
                        onChange={setQuickAddAssignees}
                        placeholder={t('plan.selectAssignees')}
                        size="sm"
                      />
                      
                      {/* Priority */}
                      <Select value={quickAddPriority} onValueChange={setQuickAddPriority}>
                        <SelectTrigger className="h-9 text-xs w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">
                            <div className="flex items-center gap-2">
                              <span className="w-4 h-4 rounded-full bg-zinc-200 flex items-center justify-center text-[10px]">○</span>
                              <span>{t('plan.priority.low')}</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="medium">
                            <div className="flex items-center gap-2">
                              <span className="w-4 h-4 rounded-full bg-[#FEF3C7] text-[#B45309] flex items-center justify-center text-[10px]">◐</span>
                              <span>{t('plan.priority.medium')}</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="high">
                            <div className="flex items-center gap-2">
                              <span className="w-4 h-4 rounded-full bg-[#FEE2E2] text-[#D93025] flex items-center justify-center text-[10px]">●</span>
                              <span>{t('plan.priority.high')}</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => { 
                            setQuickAddDay(null); 
                            setQuickAddTitle(''); 
                            setQuickAddAssignees([]);
                            setQuickAddPriority('medium');
                          }} 
                          className="h-8 flex-1 text-xs"
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => handleQuickAdd(day)} 
                          disabled={!quickAddTitle.trim()}
                          className="h-8 flex-1 text-xs bg-[#D93025] hover:bg-[#B91C1C]"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {t('common.save')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setQuickAddDay(day)}
                      className="w-full p-2 border-2 border-dashed border-zinc-200 rounded-xl text-zinc-400 hover:border-[#D93025] hover:text-[#D93025] transition-colors flex items-center justify-center gap-1 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      {t('plan.addTask')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Team Members doing tasks this week */}
        <div className="mt-8 bg-white rounded-2xl border border-zinc-200 p-6">
          <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-zinc-500" />
            {t('plan.teamAssignments')}
          </h3>
          <div className="flex flex-wrap gap-3">
            {teamAssignments.map(({ user: u, userTasks, completed }) => (
              <div key={u.id} className="flex items-center gap-3 bg-zinc-50 rounded-xl p-3 pr-5">
                <UserAvatar user={u} size="lg" />
                <div>
                  <div className="font-medium text-zinc-900">{u.name}</div>
                  <div className="text-xs text-zinc-500">{completed}/{userTasks.length} {t('plan.tasksCompleted')}</div>
                </div>
              </div>
            ))}
            {teamAssignments.length === 0 && (
              <p className="text-zinc-400 text-sm">{t('plan.noAssignments')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby="task-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedTeam?.color || TEAMS[0].color} flex items-center justify-center text-white`}>
                {selectedTeam ? <selectedTeam.icon className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              </div>
              {editingTask ? t('plan.editTask') : t('plan.newTask')}
            </DialogTitle>
            <p id="task-dialog-description" className="sr-only">
              {editingTask ? t('plan.editTask') : t('plan.createTask')}
            </p>
          </DialogHeader>
          
          <div className="space-y-5 py-2">
            {/* Title */}
            <div>
              <Label htmlFor="title" className="text-sm font-medium">{t('plan.taskTitle')}</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('plan.titlePlaceholder')}
                className="mt-2 h-12"
                data-testid="task-title-input"
              />
            </div>
            
            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-sm font-medium">{t('plan.description')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('plan.descriptionPlaceholder')}
                className="mt-2 resize-none"
                rows={2}
              />
            </div>
            
            {/* Day & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">{t('plan.day')}</Label>
                <Select value={formData.day} onValueChange={(v) => setFormData({ ...formData, day: v })}>
                  <SelectTrigger className="mt-2 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((day, i) => (
                      <SelectItem key={day} value={day}>
                        {format(addDays(currentWeek, i), 'EEEE', { locale: getLocale() })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium">{t('plan.priority.label')}</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="mt-2 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className={`px-2 py-0.5 rounded ${p.color}`}>{t(`plan.priority.${p.id}`)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Assignees (Multi) */}
            <div>
              <Label className="text-sm font-medium">{t('plan.assignees')}</Label>
              <div className="mt-2">
                <MultiUserSelect
                  users={users}
                  value={formData.assigned_to}
                  onChange={(ids) => setFormData({ ...formData, assigned_to: ids })}
                  placeholder={t('plan.selectAssignees')}
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">{t('plan.assigneesHint')}</p>
            </div>

            {/* Recurrence */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <Repeat className="w-4 h-4 text-zinc-500" />
                {t('recurrence.label')}
              </Label>
              <Select
                value={formData.recurrence_type}
                onValueChange={(v) => setFormData({ ...formData, recurrence_type: v })}
              >
                <SelectTrigger className="mt-2 h-12" data-testid="recurrence-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('recurrence.type.none')}</SelectItem>
                  <SelectItem value="daily">{t('recurrence.type.daily')}</SelectItem>
                  <SelectItem value="weekly">{t('recurrence.type.weekly')}</SelectItem>
                  <SelectItem value="monthly">{t('recurrence.type.monthly')}</SelectItem>
                  <SelectItem value="interval">{t('recurrence.type.interval')}</SelectItem>
                </SelectContent>
              </Select>

              {formData.recurrence_type === 'daily' && (
                <label className="flex items-center gap-2 mt-2 text-sm text-zinc-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.recurrence_daily_weekdays_only}
                    onChange={(e) => setFormData({ ...formData, recurrence_daily_weekdays_only: e.target.checked })}
                    className="rounded"
                    data-testid="recurrence-weekdays-only"
                  />
                  {t('recurrence.weekdaysOnly')}
                </label>
              )}
              {formData.recurrence_type === 'interval' && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-zinc-600">{t('recurrence.every')}</span>
                  <Input
                    type="number"
                    min="1"
                    max="26"
                    value={formData.recurrence_interval_weeks}
                    onChange={(e) => setFormData({ ...formData, recurrence_interval_weeks: e.target.value })}
                    className="w-20 h-10"
                    data-testid="recurrence-interval"
                  />
                  <span className="text-sm text-zinc-600">{t('recurrence.weeks')}</span>
                </div>
              )}
              {formData.recurrence_type !== 'none' && (
                <div className="mt-2">
                  <Label className="text-xs text-zinc-500">{t('recurrence.endDateOptional')}</Label>
                  <Input
                    type="date"
                    value={formData.recurrence_end_date || ''}
                    onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                    className="mt-1 h-10"
                    data-testid="recurrence-end-date"
                  />
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full px-6">
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSaveTask}
              className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6"
            >
              {editingTask ? t('common.save') : t('plan.createTask')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task detail with comments */}
      <TaskDetailDialog
        task={detailTask}
        open={!!detailTask}
        onOpenChange={(o) => { if (!o) setDetailTask(null); }}
        users={users}
      />
    </Layout>
  );
};

export default PlanPage;
